export default class PathAnimation {
  constructor({ reverseClass = 'js-kv_reverse', pathColorEasing = 'ease-in-out' } = {}) {
    this.reverseClass = reverseClass;
    this.pathColorEasing = pathColorEasing;
  }

  applyPathDash(path, { dasharray, offset }) {
    if (dasharray !== undefined) {
      path.style.strokeDasharray = dasharray;
      path.setAttribute('stroke-dasharray', dasharray);
    }

    if (offset !== undefined) {
      const offsetValue = this.formatDashOffset(offset);
      path.style.strokeDashoffset = offsetValue;
      path.setAttribute('stroke-dashoffset', offsetValue);
    }
  }
  // stroke-dasharray / stroke-dashoffset を最終状態として固定する
  // animation.cancel() すると、アニメーション由来のスタイルが外れることがあるため
  settlePathDash(path, dash) {
    if (Number.isFinite(dash)) {
      this.applyPathDash(path, { dasharray: `${dash} ${dash}`, offset: 0 });
      return;
    }

    this.applyPathDash(path, { offset: 0 });
  }

  formatDashOffset(value) {
    const rounded = Math.round(value * 100) / 100;
    return `${rounded}`;
  }

  showPathLoopLayer(path) {
    path.style.removeProperty('visibility');
    path.style.opacity = '1';
  }

  hidePathLoopLayer(path) {
    path.style.removeProperty('visibility');
    path.style.opacity = '0';
  }

  // pathに設定された全てのanimationをキャンセルする
  cancelPathAnimations(path) {
    path.getAnimations().forEach((animation) => {
      this.disposePathAnimation(animation);
    });
  }

  // 引数のアニメーションをキャンセルする
  disposePathAnimation(animation) {
    if (!animation) {
      return;
    }

    animation.cancel();
  }

  bindAnimationFinish(animation, handler) {
    if (!animation) {
      return;
    }

    let called = false;
    const run = () => {
      if (called) {
        return;
      }

      called = true;
      handler();
    };

    animation.onfinish = run;

    if (typeof animation.finished?.then === 'function') {
      animation.finished.then(run).catch(() => {});
    }
  }

  applyPathStrokeOpacity(path) {
    const strokeOpacity = path.getAttribute('stroke-opacity');
    if (strokeOpacity === null) {
      return;
    }

    path.style.strokeOpacity = strokeOpacity;
  }

  getDrawStartOffset(dash, isReverse) {
    return isReverse ? -dash : dash;
  }

  getEraseEndOffset(dash, isReverse) {
    return isReverse ? dash : -dash;
  }

  getPathDashMeta(path, loopReverse) {
    const dash = path.getTotalLength();
    let isReverse;

    if (loopReverse === true) {
      isReverse = true;
    } else if (loopReverse === false) {
      isReverse = false;
    } else {
      isReverse = path.classList.contains(this.reverseClass);
    }

    return { dash, isReverse };
  }

  animatePathDraw(path, meta, duration, options = {}) {
    const { dash, isReverse } = meta;
    const startOffset = this.getDrawStartOffset(dash, isReverse);
    const { easing = 'ease-in-out', onComplete } = options;

    this.showPathLoopLayer(path);
    this.applyPathStrokeOpacity(path);
    this.cancelPathAnimations(path);
    const animation = path.animate(
      [
        { strokeDashoffset: startOffset },
        { strokeDashoffset: 0 }
      ],
      {
        duration,
        easing,
        fill: 'forwards'
      }
    );
    this.bindAnimationFinish(animation, () => {
      this.settlePathDash(path, dash);
      this.applyPathStrokeOpacity(path);
      this.disposePathAnimation(animation);
      if (onComplete) {
        onComplete();
      }
    });
  }

  animatePathErase(path, meta, duration, options = {}) {
    const { dash, isReverse } = meta;
    this.applyPathDash(path, { dasharray: `${dash} ${dash}` });
    const endOffset = this.getEraseEndOffset(dash, isReverse);
    const { easing = 'ease-in-out', onComplete } = options;

    this.showPathLoopLayer(path);
    this.applyPathStrokeOpacity(path);
    this.cancelPathAnimations(path);
    const animation = path.animate(
      [
        { strokeDashoffset: 0 },
        { strokeDashoffset: endOffset }
      ],
      {
        duration,
        easing,
        fill: 'forwards'
      }
    );
    this.bindAnimationFinish(animation, () => {
      this.applyPathDash(path, { offset: endOffset });
      this.applyPathStrokeOpacity(path);
      this.disposePathAnimation(animation);
      if (onComplete) {
        onComplete();
      }
    });
  }

  animatePathSwap(fromPath, fromMeta, toPath, toMeta, duration, onComplete) {
    this.applyPathDash(toPath, {
      offset: this.getDrawStartOffset(toMeta.dash, toMeta.isReverse)
    });
    const easing = this.pathColorEasing;
    let completed = 0;
    const finish = () => {
      completed += 1;
      if (completed < 2) {
        return;
      }

      this.applyPathDash(fromPath, {
        offset: this.getEraseEndOffset(fromMeta.dash, fromMeta.isReverse)
      });
      this.hidePathLoopLayer(fromPath);
      this.settlePathDash(toPath, toMeta.dash);
      this.showPathLoopLayer(toPath);
      this.applyPathStrokeOpacity(toPath);
      if (onComplete) {
        onComplete();
      }
    };

    this.animatePathDraw(toPath, toMeta, duration, { easing, onComplete: finish });
    this.animatePathErase(fromPath, fromMeta, duration, { easing, onComplete: finish });
  }
}
